import { Header, SpaceBetween, Tabs } from '@amzn/awsui-components-console';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { fetchPipelineActivitiesAndInstances, fetchInstancesByPipeline } from '../../data/api/fetchFromAPI';
import PAGES from '../../nav/Pages';
import PipelineActionsDropdown from './PipelineActionsDropdown';
import PipelineToggleStatusButton from './PipelineToggleStatusButton';
import PipelineContainerLayout from './PipelineContainerLayout';
import PipelineActivitiesTable from './PipelineActivitiesTable';
import MonitorPipeline from './MonitorPipeline';
import { HammerstoneAppLayout } from '../helpers';
import { useGroupName, usePipeline } from 'src/data/redux';
import { useAddPipelineToRecentPage, useConditionalEffect, useTimeoutListRef } from 'src/commons';
import { DisplayMode } from '../helpers/content/contentInterfaces';
import { urlSearchString } from 'src/nav/navHelper';
import { dateRangeToStartAndEndDatetimes } from '../instance/InstanceDateRangePicker';
import { GetInstanceInfoByPipelineNameRequest } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';

enum ViewPipelineAspect {
	Details = 'details',
	Monitor = 'monitor',
}

function getPagePath(aspect: string): string {
	switch (aspect) {
		case ViewPipelineAspect.Monitor:
			return PAGES.MONITOR_PIPELINE.path;
		case ViewPipelineAspect.Details:
		default:
			return PAGES.VIEW_PIPELINE.path;
	}
}

export default function ViewPipeline(props: { pipelineName: string; aspect: `${ViewPipelineAspect}` }) {
	const { pipelineName, aspect } = props;
	const groupName = useGroupName();
	const { data: pipeline, fetching } = usePipeline({ groupName, pipelineName });
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const rawDateRange = searchParams.get('dateRange');
	const rawInstanceStatusId = searchParams.get('instanceStatusId');
	const dateRange = JSON.parse(rawDateRange || '{}');
	// TODO implement support for filter by instanceStatusId, this needs update on HammerstoneAPI
	const instanceStatusId = parseInt(rawInstanceStatusId) || -1;
	const [startScheduleDateTime, endScheduleDateTime] = dateRangeToStartAndEndDatetimes(dateRange);
	const instanceInput: GetInstanceInfoByPipelineNameRequest = {
		pipelineName,
		groupName,
		startScheduleDateTime: startScheduleDateTime,
		endScheduleDateTime: endScheduleDateTime,
	};

	const delayRefs = useTimeoutListRef();

	useAddPipelineToRecentPage(pipelineName, groupName);

	useConditionalEffect(() => {
		fetchPipelineActivitiesAndInstances(pipelineName, groupName, { delays: delayRefs }, false);
	}, [pipelineName, groupName]);

	useEffect(() => {
		if (pipelineName && groupName) {
			fetchInstancesByPipeline(instanceInput, true);
		}
	}, [pipelineName, groupName, rawDateRange, rawInstanceStatusId]);

	function getPageLayoutProps() {
		switch (aspect) {
			case ViewPipelineAspect.Monitor:
				return PAGES.MONITOR_PIPELINE.pageLayoutProps({ pipelineName, groupName });
			case ViewPipelineAspect.Details:
			default:
				return PAGES.VIEW_PIPELINE.pageLayoutProps({ pipelineName, groupName });
		}
	}

	return (
		<HammerstoneAppLayout {...getPageLayoutProps()}>
			<SpaceBetween size="xs">
				<Header
					variant="h1"
					actions={
						<SpaceBetween size="xs" direction="horizontal">
							{/* TODO: Update iconography to use more friendly values (play/pause, activate?) */}
							<PipelineToggleStatusButton pipelineName={pipelineName} />
							<PipelineActionsDropdown pipelineName={pipelineName} />
						</SpaceBetween>
					}
				>
					{pipelineName}
				</Header>
				<Tabs
					tabs={[
						{
							label: 'Details',
							id: ViewPipelineAspect.Details,
							content: (
								<SpaceBetween size="xl">
									<PipelineContainerLayout
										pipelineName={pipelineName}
										mode={fetching || !pipeline ? DisplayMode.Loading : DisplayMode.View}
									/>
									<PipelineActivitiesTable pipelineName={pipelineName} />
								</SpaceBetween>
							),
						},
						{
							label: 'Monitor',
							id: ViewPipelineAspect.Monitor,
							content: <MonitorPipeline pipelineName={pipelineName} />,
						},
					]}
					activeTabId={aspect}
					onChange={({ detail }) => {
						navigate({
							pathname: getPagePath(detail.activeTabId).replace(':pipelineName', pipelineName),
							search: urlSearchString({ groupName }),
						});
					}}
				/>
			</SpaceBetween>
		</HammerstoneAppLayout>
	);
}
